import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { API, EndpointStatisticsDTO } from '../../../../services/api/api';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { ErrorService } from '../../../../services/error.service';
import { UtilService } from '../../../../services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestClass } from '../../../../services/global/RequestClass';
import { FilterPanelConfig } from '@ngx-seatback/layout';

@Component({
  selector: 'sb-page-endpoint-performance',
  templateUrl: './endpoint-performance.component.html',
  styleUrls: ['./endpoint-performance.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EndpointPerformanceComponent extends RequestClass {
  public readonly modal = inject(ModalService);
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly translate = inject(TranslateService);
  private readonly api = inject(API);
  private readonly destroyRef = inject(DestroyRef);

  // table wrapper variables
  endpoints: EndpointStatisticsDTO[] = [];
  displayedEndpoints: EndpointStatisticsDTO[] = [];

  // filter variables
  searchStr: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: false,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    const sub = this.api
      ._admin_AdminEndpoints_endpointStatistics()
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (endpointData: EndpointStatisticsDTO[]) => {
          this.endpoints = endpointData;
          this.search();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  search() {
    if (this.searchStr) {
      this.displayedEndpoints = [];
      for (const query of this.endpoints) {
        if (
          (
            query.resourceDescriptor.packageName.toLowerCase() +
            '.' +
            query.resourceDescriptor.resourceName.toLowerCase() +
            '.' +
            query.resourceDescriptor.methodName.toLowerCase()
          ).includes(this.searchStr.toLowerCase())
        ) {
          this.displayedEndpoints.push(query);
        }
      }
    } else {
      this.displayedEndpoints = this.endpoints;
    }
    this.sort();
  }

  sort() {
    let sorted = Object.assign([], this.displayedEndpoints);
    if (this.displayedEndpoints.length > 0) {
      sorted = sorted.sort((a, b) =>
        (a.resourceDescriptor.packageName + '.' + a.resourceDescriptor.resourceName + '.' + a.resourceDescriptor.methodName).localeCompare(
          b.resourceDescriptor.packageName + '.' + b.resourceDescriptor.resourceName + '.' + b.resourceDescriptor.methodName
        )
      );
    }
    this.displayedEndpoints = sorted;
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.search();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.searchStr = filterMap.get('search')?.[0];
  }
}
